import { Send } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useState } from "react";
import { CalendarIcon, MessageIcon } from "../../../../../components/icons";
import { BookingData } from "../../../types/view-booking";
import { CoverPageTemplate } from "../../types/cover.types";
import { Proposal, ProposalActionItem } from "../../types/proposal.types";
import ProposalBanner from "./components/ProposalBanner";
import { useProposals } from "./components/proposals-page/hooks/UseProposals";
import ProposalList from "./components/proposals-page/Proposallist";
import ProposalSidebar from "./components/proposals-page/ProposalSideBar";
import CreateProposalPage from "./CreateProposal";
import ViewProposalPage from "./ViewProposalPage";

const actionItems: ProposalActionItem[] = [
  {
    icon: <CalendarIcon sx={{ color: "#007AFF" }} />,
    title: "30 days from Lisa Zeremende's aniniversssary",
    subtitle: "Send message to Lisa's family",
  },
  {
    icon: <MessageIcon sx={{ color: "#E11D48" }} />,
    title: "Request Client review",
    subtitle: "Gather insights from client for future improvements",
  },
  {
    icon: <Send sx={{ color: "#724E41" }} />,
    title: "Send Thank you message to client",
    subtitle: "Reflect on project outcomes and team",
  },
];

interface ProposalTabProps {
  bookingData: BookingData | null;
}

export default function ProposalTab({ bookingData }: ProposalTabProps) {
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(
    null,
  );
  const [selectedCoverTemplate, setSelectedCoverTemplate] = useState<CoverPageTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    proposals,
    loading,
    refetch,
    totalCount,
    nextUrl,
    prevUrl,
    goToNext,
    goToPrev,
    setProposals,
  } = useProposals();
  const [previousStreams, setPreviousStreams] = useState<
    { id?: string; title: string; url: string }[]
  >([]);


  const defaultServices = bookingData?.package?.items?.map((item) => ({
    service: {
      id: item.id,
      name: item.name,
      price: item.price,
      description: "",
      service_category: "",
      service_category_display: "",
      customizable: false,
      features: [],
    },
    quantity: 1,
  }));

  const selectedProposal = proposals.find((p) => p.id === selectedProposalId);

  const clientName = bookingData?.organiser?.name || "";

  const deceasedName = bookingData?.beloved?.name || "";

  const email = bookingData?.organiser?.email || "";

  const phone = bookingData?.organiser?.phone || "";

  const address = bookingData?.organiser?.address || "";

  const handleStatusChange = (id: number, newStatus: string) => {
    setProposals((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, status: newStatus as Proposal["status"] } : p,
      ),
    );
  };

  if (isCreating) {
    return (
      <CreateProposalPage
      onTemplateChange={setSelectedCoverTemplate}
        defaultValues={{
          clientName,
          deceasedName,
          email,
          phone,
          address,
          packageName: bookingData?.package?.name,
          serviceType: bookingData?.service,
          bookingId: bookingData?.id,
          defaultServices,
        }}
        onSave={() => {
          setIsCreating(false);
          refetch();
        }}
        onSendToClient={() => {
          setIsCreating(false);
          refetch();
        }}
        onFinishLater={() => {
          setIsCreating(false);
          refetch();
        }}
        onStreamsChange={setPreviousStreams}
      />
    );
  }

  if (isUpdating && selectedProposal) {
    return (
      <CreateProposalPage
      onTemplateChange={setSelectedCoverTemplate}
        defaultValues={{
          clientName,
          deceasedName,
          email,
          phone,
          address,
          packageName: selectedProposal.package?.name,
          serviceType: bookingData?.service,
          bookingId: bookingData?.id,
          proposalId: selectedProposal.id,
          introductionLetter: selectedProposal.introduction_letter,
          termsAndConditions: selectedProposal.terms_and_conditions,
          defaultServices: selectedProposal.services?.map((s) => ({
            service: {
              id: s.id,
              name: s.name,
              price: Number(s.price),
              description: s.description ?? "",
              service_category: s.service_category,
              service_category_display: s.service_category,
              customizable: false,
              features: [],
            },
            quantity: 1,
          })),
        }}
        onSave={() => {
          setIsUpdating(false);
          refetch();
        }}
        onFinishLater={() => {
          setIsUpdating(false);
          refetch();
        }}
        onStreamsChange={setPreviousStreams}
      />
    );
  }

  if (selectedProposal) {
    return (
      <ViewProposalPage
      selectedCoverTemplate={selectedCoverTemplate}
        proposal={selectedProposal}
        onBack={() => setSelectedProposalId(null)}
        onUpdateProposal={() => setIsUpdating(true)}
        onGenerateInvoice={() => console.log("generate invoice")}
        onConfirmBooking={() => console.log("confirm booking")}
        status={selectedProposal.status}
        onStatusChange={handleStatusChange}
        //activityLog={[]}
        previousStreams={previousStreams}
      />
    );
  }

  return (
    <Box>
      <ProposalBanner onAction={() => console.log("banner action")} />

      <Box sx={{ display: "flex", gap: 2 }}>
        <ProposalList
          proposals={proposals}
          loading={loading}
          totalCount={totalCount}
          nextUrl={nextUrl}
          prevUrl={prevUrl}
          onNext={goToNext}
          onPrev={goToPrev}
          onCreateProposal={() => setIsCreating(true)}
          onViewProposal={(id) => setSelectedProposalId(id)}
        />
        <ProposalSidebar actionItems={actionItems} bookingData={bookingData}/>
      </Box>
    </Box>
  );
}
